// FLAG:useDocuAi — the central invoice-review component embedded in the document
// drawer's Details tab. Same UX as development's legacy details tab (drawer stays open, no
// route change), but the tab content is the shared ReceiptEdit review form (form column only —
// the drawer already shows the document preview on its left). The drawer footer's Save button
// triggers the review save through the forwarded ref (see document-drawer-footer.tsx).
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import Loader from '@/modules/base/components/loader/loader'
import ReceiptReviewWorkspace from '@/modules/receipt/review/receipt-review-workspace'
import FreshAnalysisBanner from '@/modules/document/components/review/fresh-analysis-banner'
import InvoiceAnalysisPlaceholder from '@/modules/receipt/review/invoice-analysis-placeholder'
import { getInvoiceAnalysisState, hasInvalidValidationResult, INVOICE_ANALYSIS_STATE } from '@/modules/document/utils/invoice-analysis-state'
// FLAG:useDocuAi — makes the time-based age limit effective (see its rationale).
import { useAnalysisBlinkCap } from '@/modules/document/utils/use-analysis-blink-cap'
import { buildReviewData } from '@/modules/document/components/review/build-review-data'
import { useAdoptedAnalysis } from '@/modules/document/components/review/use-adopted-analysis'
// FLAG:useDocuAi — preselect the booking decision only when there is hard evidence (see there).
import { resolveBookingTargetEvidence } from '@/modules/document/components/review/booking-target-evidence'
// FLAG:useDocuAi — "review again" from the Documents drawer; the SAME hook used by the review page
// and the expense host, so all three send the reReviewSave marker and ask the identical question
// about a renamed payee. Without it, a re-review saved here would write the corrected values to the
// document while the expense silently kept the old ones.
import ReReviewConfirmDialog from '@/modules/document/components/review/re-review-confirm-dialog'
import { useReReviewSave } from '@/modules/document/components/review/use-re-review-save'
import FileService from '@/modules/file/fileService'
import { user } from 'ampersand-app'
import { USER_RIGHTS, SUBSCRIPTION_MODULES, experimentalFeatures } from '@/modules/base/util/constants'
import { BookingTarget, resolveReviewBookingTarget } from '@/modules/receipt/review/review-target' // FLAG:useDocuAi
import { assignFile, clearFiles, resetData, setAndValidateData } from '@/redux/ducks/receipt/edit'
import { closeDocumentDrawer } from '@/redux/ducks/document/drawer'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useUpdateSearchParams } from '@/modules/_helpers/updateSearchParams'
import { toast } from '@/modules/base/components/toast/toastConfig'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'
import {
  shouldOfferDocumentBookingTarget,
  shouldShowBookingTargetChoice,
  stripUnofferedDocumentTarget,
  shouldShowCreateExpenseCheckbox,
} from '@/modules/receipt/helpers/invoice-review-config'
import { TAG_TYPES } from '@/modules/file/constants'
import {
  buildDocumentInvoiceReviewTags,
  getDocumentInvoiceKind,
  getDocumentInvoiceKindLabel,
} from '@/modules/document/utils/document-invoice-kind'

const useStyles = makeStyles(() => ({
  content: {
    height: '100%',
  },
}))

type DocumentInvoiceReviewDrawerTabProps = {
  refreshList?: () => void,
}

const DocumentInvoiceReviewDrawerTab = forwardRef((props: DocumentInvoiceReviewDrawerTabProps, ref) => {
  const { refreshList } = props
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const drawerDocument = useAppSelector((state) => state.document.drawer.document)
  const { data: formData } = useAppSelector(state => state.receipt.edit)
  const hasExpenseWriteRights = useCheckAccessRights(USER_RIGHTS.EXPENSES.WRITE)
  // FLAG:useDocuAi — F18: hide "Ausgabe erstellen" for a credit note / cancellation invoice
  // (server refuses it too).
  const isCancellationInvoice = getDocumentInvoiceKind(drawerDocument) === TAG_TYPES.CANCELLATION
  const canCreateExpenseFromReview = shouldShowCreateExpenseCheckbox(hasExpenseWriteRights, isCancellationInvoice)
  // FLAG:useDocuAi — the same booking decision as on the review page, using the same rule. The flag
  // appears explicitly in the condition even though this tab is mounted only with the flag ON:
  // visibility is therefore tied to useDocuAi BY CONSTRUCTION, not by an assumption about who
  // renders the component.
  const hasRevenueModule = useCheckModuleAccess(SUBSCRIPTION_MODULES.REVENUES)
  const hasRevenueWriteRight = useCheckAccessRights(USER_RIGHTS.REVENUES.WRITE)
  const [, updateSearchParams] = useUpdateSearchParams()
  const documentId = drawerDocument?.id
  const [file, setFile] = useState(null)
  const [reviewData, setReviewData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [createExpenseFromReview, setCreateExpenseFromReview] = useState(false)
  // FLAG:useDocuAi — the reviewer's own click on the booking checkbox; `null` = not yet touched
  // (rationale in document-invoice-review-page.tsx).
  const [bookingTargetChoice, setBookingTargetChoice] = useState<BookingTarget | null>(null)
  // FLAG:useDocuAi — after adopting a newer analysis, rerun the same loading effect instead of
  // patching local state: from that point on, what the reviewer edits IS what the server wrote (the
  // same rule as for reset on the review page).
  const [reloadNonce, setReloadNonce] = useState(0)
  // …and do the same when triggered from OUTSIDE this component: „In Prüfung zurücksetzen" is in
  // the drawer footer („Weitere Funktionen"), not here, but changes exactly the data this tab shows.
  // Because neither the document changes nor `reloadNonce` increases, the loading effect never ran
  // again — the review kept showing the pre-reset values (DMS report 4c28c454). The counter from the
  // drawer slice is that signal.
  const reviewReloadNonce = useAppSelector((state) => state.document.drawer.reviewReloadNonce)
  // FLAG:useDocuAi — did the run the reviewer started take its result over? (see use-adopted-analysis)
  const { rememberSeeded, hasAdoptedNewerAnalysis } = useAdoptedAnalysis()

  // FLAG:useDocuAi — is an analysis currently running for THIS document? Deliberately read from the
  // LIVE document in the Redux drawer, not local `file`: the latter is a mount-time snapshot and
  // knows nothing about subsequent progress. The drawer record, in contrast, is updated by the WS
  // events (analysisStarted / terminal).
  const analysisState = getInvoiceAnalysisState(drawerDocument)
  const analysisRunning = analysisState === INVOICE_ANALYSIS_STATE.ANALYZING
    || analysisState === INVOICE_ANALYSIS_STATE.RETRYING
  // FLAG:useDocuAi — the age limit in getInvoiceAnalysisState passes with TIME but is evaluated
  // during rendering. Without this tick, it is never checked in the critical case: if the terminal
  // event is missing, there is no Redux update to trigger a render either — and the display remained
  // on "analysis is running..." until the user touched something else. Rationale and tick interval are in
  // use-analysis-blink-cap.
  useAnalysisBlinkCap(analysisRunning)

  // Reload when the SERVER changed this review while the tab is mounted. Two signals, both read
  // from the live drawer record: the transition "was running" → "is no longer running" (a run the
  // reviewer started), and a changed terminal marker `analysisDoneAt` WITHOUT a running phase in
  // between — a result that arrived unsolicited (redelivered event, DMS re-ingest, a DocuAI-side
  // reprocess) or a refresh the DMS answered from its stored contract. Without the second signal
  // the open drawer kept showing the old positions after such an arrival and only a page reload
  // showed the truth (Meldung 44e29660, IPD).
  //
  // What happens then depends on the review AS LOADED (`seededRef`), because the server treats the
  // two states differently (analyzeFile → upsertFromAnalysis):
  //   - a review still in 'review' is REPLACED by every terminal result (the F3 guard covers only
  //     'complete'/'partial'), so the form's values are stale by definition → reseed from the
  //     server. Same rule as reset: what the reviewer edits from here on IS what the server wrote.
  //   - a reviewed row keeps the reviewer's values: reload the file metadata ONCE so the
  //     newer-analysis notice can appear (`freshAnalysisAt`), and reseed ONLY when the server
  //     adopted the run (`analysisAdoptedAt`, see use-adopted-analysis) — a run the reviewer
  //     started through „Erneut analysieren" has already replaced those values server-side, and
  //     without the reseed the old values keep standing and, once the positions were deleted, no
  //     new analysis would ever bring them back. An UNSOLICITED run leaves the reviewer's values and
  //     their unsaved input alone and merely offers the banner.
  const wasAnalysingRef = useRef(false)
  const seededRef = useRef<{ analysisDoneAt: string | null, reviewed: boolean } | null>(null)
  const terminalMarker = drawerDocument?.metaData?.analysisDoneAt ?? null
  useEffect(() => {
    if (analysisRunning) {
      wasAnalysingRef.current = true
      return undefined
    }
    const wasRunning = wasAnalysingRef.current
    wasAnalysingRef.current = false
    const seeded = seededRef.current
    // `null` on the drawer copy is "not known here", never "a new terminal": the mount-time record
    // in the list may lack the marker the freshly fetched file carries.
    const markerChanged = !!seeded && terminalMarker !== null && terminalMarker !== seeded.analysisDoneAt
    if (!wasRunning && !markerChanged) return undefined
    if (!documentId) return undefined
    if (seeded && !seeded.reviewed && markerChanged) {
      setReloadNonce(nonce => nonce + 1)
      return undefined
    }
    let isCurrent = true
    FileService.fetchFileInfo(documentId)
      .then(({ body }) => {
        if (!isCurrent) return
        if (hasAdoptedNewerAnalysis(body)) {
          setReloadNonce(nonce => nonce + 1)
        } else {
          setFile(body)
        }
      })
      .catch(() => {})
    return () => { isCurrent = false }
  }, [analysisRunning, terminalMarker, documentId, hasAdoptedNewerAnalysis])

  useEffect(() => {
    if (!documentId) { return undefined }

    let isMounted = true

    // Same clean-slate rule as the full review page: stale state.receipt.edit from a previous
    // expense/receipt edit would bleed into the review form via the finalData merge.
    dispatch(resetData())
    dispatch(clearFiles())
    setIsLoading(true)
    setReviewData(null)
    setCreateExpenseFromReview(false)
    setBookingTargetChoice(null)

    // Fetch fresh file info (like the review page) instead of trusting the drawer document —
    // the drawer copy can carry unsaved drawer edits and misses review-only enrichments.
    FileService.fetchFileInfo(documentId)
      .then(async({ body }) => {
        if (!isMounted) return
        const nextReviewData = await buildReviewData(body)
        if (!isMounted) return
        setFile(body)
        // The form now holds exactly this server state; an adoption after this one has to reseed
        // it (the terminal effect above).
        rememberSeeded(body)
        seededRef.current = {
          analysisDoneAt: body?.metaData?.analysisDoneAt ?? null,
          reviewed: body?.documentInvoiceDetails?.reviewStatus === INVOICE_ANALYSIS_STATE.COMPLETE
            || body?.documentInvoiceDetails?.reviewStatus === INVOICE_ANALYSIS_STATE.PARTIAL,
        }
        setReviewData(nextReviewData)
        // FLAG:useDocuAi — seed state.receipt.edit with the reviewed analysis data as the LAST
        // write before the workspace renders, so isFormValid turns true and the drawer footer's
        // Save button is enabled without the operator having to touch a field (the drawer footer
        // disables Save on !reviewFormValid). Identical seed order to the full review page
        // (document-invoice-review-page.tsx:100-102).
        dispatch(resetData())
        dispatch(setAndValidateData(nextReviewData))
        dispatch(assignFile({ ...body, fileId: body.id }))
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
      dispatch(resetData())
      dispatch(clearFiles())
    }
  }, [dispatch, documentId, reloadNonce, reviewReloadNonce])

  useEffect(() => {
    if (!canCreateExpenseFromReview && createExpenseFromReview) {
      setCreateExpenseFromReview(false)
    }
  }, [canCreateExpenseFromReview, createExpenseFromReview])

  const baseData = useMemo(() => reviewData || {}, [reviewData])
  const finalData = useMemo(() => ({
    ...baseData,
    ...formData,
    metaData: {
      ...baseData.metaData,
      ...formData.metaData,
    },
    filesToAssign: baseData.filesToAssign,
    receipts: baseData.receipts,
    bookings: formData.bookings || baseData.bookings,
    readOnly: false,
  }), [baseData, formData])
  const invoiceKind = useMemo(() => getDocumentInvoiceKind(file, finalData), [file, finalData.metaData?.documentType])
  const invoiceKindLabel = getDocumentInvoiceKindLabel(invoiceKind)
  // FLAG:useDocuAi — preselection only with hard evidence; rationale in booking-target-evidence.ts.
  const bookingTargetEvidence = useMemo(
    () => resolveBookingTargetEvidence(file, finalData),
    [file, finalData.documentDirection, finalData.documentDirectionMatch, finalData.metaData?.documentType],
  )
  // FLAG:useDocuAi — named once: the same fact decides whether the choice appears AND whether it
  // may offer „Nur Dokument“. The dropzone on the Ausgaben/Einnahmen LIST lands in this host via
  // /documents/review/:id?returnTo=/expenses, so „hochgeladen über Ausgaben“ has to be answered here too.
  const hasUploadIntent = !!file?.metaData?.receiptUploadAction && !(file?.metaData?.receiptAccountingRemoval || file?.metaData?.receiptAccountingRestoreTarget)
  const offersDocumentTarget = shouldOfferDocumentBookingTarget({ hasUploadIntent })
  // FLAG:useDocuAi — live projection of the server rule (see review-target.ts); the reviewer's own
  // click takes precedence, followed by the persisted value and then the preselection. The review
  // page uses the same order.
  const reviewBookingTarget = resolveReviewBookingTarget({
    useDocuAiEnabled: !!user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi),
    receiptUploadAction: file?.metaData?.receiptUploadAction,
    receiptAccountingRestoreTarget: file?.metaData?.receiptAccountingRestoreTarget,
    isAccountingDetached: !!(file?.metaData?.receiptAccountingRemoval || file?.metaData?.receiptAccountingRestoreTarget),
    bookingTarget: bookingTargetChoice ?? (file?.metaData?.receiptAccountingRemoval ? 'document' : stripUnofferedDocumentTarget(finalData.bookingTarget, offersDocumentTarget) ?? bookingTargetEvidence?.target),
    reviewPayeeRole: finalData.reviewPayee?.role,
  })
  const reviewReceiptType = reviewBookingTarget === 'revenue' ? 'revenue' : 'expense'
  const bookingTargetHint = file?.metaData?.receiptAccountingRemoval || bookingTargetChoice || finalData.bookingTarget || !bookingTargetEvidence
    ? null
    : lang.documents.reviewBookingTarget.reason[bookingTargetEvidence.reason]
  const showBookingTargetChoice = shouldShowBookingTargetChoice({
    useDocuAiEnabled: !!user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi),
    hasUploadIntent,
    hasRevenueModule,
    hasRevenueWriteRight,
  })

  // FLAG:useDocuAi — the drawer footer's Save calls `onSave()` on the ref this component exposes.
  // That ref used to be the workspace's own imperative handle (save immediately). It now goes
  // through requestSave: a FIRST review still saves straight through; re-reviewing an already
  // reviewed invoice first opens the confirmation dialog (what happens to the existing expense,
  // renamed payee) and only saves once the reviewer confirmed.
  const reviewRef = useRef<{ onSave:() => void }>(null)
  const startSaving = useCallback(() => {
    // Deferred by a tick for the same reason the expense host defers it: the party answer travels
    // to the save as a prop, so the workspace must re-render with it before onSave reads the data.
    window.setTimeout(() => reviewRef.current?.onSave(), 1)
  }, [])
  const { isReReview, partyResolution, requestSave, dialogProps } = useReReviewSave({
    fileId: documentId,
    // Keyed on the status as LOADED (`file`, not the drawer copy), so it cannot flip mid-edit.
    reviewStatus: file?.documentInvoiceDetails?.reviewStatus,
    createExpense: createExpenseFromReview,
    // FLAG:useDocuAi — as on the review page: include it only when this host asks the question.
    bookingTarget: showBookingTargetChoice ? reviewBookingTarget : null,
    reviewPayee: finalData.reviewPayee,
    // FLAG:useDocuAi — as on the review page: the warning travels into the dialog, not into a second
    // modal behind it.
    invalidEInvoice: hasInvalidValidationResult(file),
    onProceed: startSaving,
  })
  useImperativeHandle(ref, () => ({ onSave: requestSave }), [requestSave])

  const handleSaveSuccess = () => {
    dispatch(resetData())
    dispatch(clearFiles())
    // Development parity: the drawer's legacy save closes the drawer after saving
    // (document-drawer-footer.tsx onClose) — the review save behaves the same.
    refreshList?.()
    updateSearchParams([{ key: 'documentId' }, { key: 'rowIndex' }])
    dispatch(closeDocumentDrawer())
  }

  // FLAG:useDocuAi — the entire tree was REPLACED by a spinner here. Every reload therefore tore
  // down the already rendered review component and rebuilt it; during „Erneut analysieren"
  // (multiple consecutive refetches), the spinner flashed repeatedly. Use the same solution as
  // on the expense side instead: ONE tree, with only the form column
  // switching to the progress bar — React reconciles by position, and changing the parent node
  // is an unmount (see the detailed rationale
  // in embedded-invoice-review.tsx).
  // …and during an ACTIVE second run, the form column shows the same progress bar as during
  // initial analysis on the expense side. The receipt beside it stays in place;
  // only the form is replaced.
  const isAnalysing = isLoading || !file || !reviewData || analysisRunning

  return (
    <>
      <PerfectScrollbar className={classes.content}>
        {/* Paid-status/payment hint now lives inside the shared ReceiptEdit review
            component (rendered for every entry point), not here. */}
        {/* FLAG:useDocuAi — a newer-analysis notice: otherwise „Erneut analysieren"
            ends invisibly because the review (correctly) retains its values. Self-gating:
            without the server marker, nothing gets rendered. */}
        <FreshAnalysisBanner file={file} onAdopted={() => setReloadNonce(nonce => nonce + 1)} />
        {isAnalysing
          ? <InvoiceAnalysisPlaceholder fullWidth />
          : <ReceiptReviewWorkspace
          // FLAG:useDocuAi — a Documents upload carries no upload intent, and this code
          // silently turned it into an expense. For a genuine self-billed credit note
          // (the customer issues the receipt), this is the wrong side. The host now follows
          // the same rule as the server: the role decides
          // when there is no upload intent.
            type={reviewReceiptType}
            data={finalData}
            documentReviewDetails={file?.documentInvoiceDetails}
            onSaveSuccess={handleSaveSuccess}
            onSaveAbort={() => {}}
            onSaveError={() => {}}
            ref={reviewRef}
            documentReviewFormOnly
            documentReviewFileId={documentId}
            showDocumentReviewCreateExpense={canCreateExpenseFromReview}
            documentReviewCreateExpense={createExpenseFromReview}
            showDocumentReviewBookingTarget={showBookingTargetChoice}
            documentReviewBookingTarget={reviewBookingTarget}
            documentReviewAllowDocumentTarget={offersDocumentTarget}
            documentReviewBookingTargetHint={bookingTargetHint}
            onDocumentReviewBookingTargetChange={setBookingTargetChoice}
            documentReviewKindLabel={invoiceKindLabel}
            documentReviewTags={buildDocumentInvoiceReviewTags(file, invoiceKind)}
            onDocumentReviewCreateExpenseChange={setCreateExpenseFromReview}
            documentReviewIsReReview={isReReview}
            documentReviewPartyResolution={partyResolution}
          />}
      </PerfectScrollbar>
      {/* FLAG:useDocuAi — a first-time review never opens this (requestSave saves straight through). */}
      <ReReviewConfirmDialog {...dialogProps} />
    </>
  )
})

DocumentInvoiceReviewDrawerTab.displayName = 'DocumentInvoiceReviewDrawerTab'

export default DocumentInvoiceReviewDrawerTab
